<script lang="ts">
  import type { Snippet } from "svelte";
  import { storageModel } from "../storage-manager";
  import {
    shouldShowNavBar,
    shouldOffsetUp,
    notifyStatusTextColor,
  } from "../chief-bridge";
  import { onMount } from "svelte";

  interface Props {
    /** 标题 */
    title?: string;
    /** 自定义标题内容（Snippet，优先级高于 title 字符串） */
    titleCustom?: Snippet;
    /** 浅色主题背景色 (hex 格式) */
    lightBgColor?: string;
    /** 深色主题背景色 (hex 格式) */
    darkBgColor?: string;
    /** 浅色主题文字颜色 */
    lightTextColor?: string;
    /** 深色主题文字颜色 */
    darkTextColor?: string;
    /** 背景透明度 (0-1)，scrollFade=true 时忽略 */
    bgOpacity?: number;
    /** 是否启用滚动渐变 */
    scrollFade?: boolean;
    /** 开始渐变的滚动距离 (px) */
    scrollThreshold?: number;
    /** 最大透明度 */
    maxOpacity?: number;
    /** 是否显示返回按钮 */
    showBack?: boolean;
    /** 返回按钮点击回调，返回 false 阻止默认行为 */
    onBack?: () => boolean | void;
    /** 关闭 H5 的回调 */
    onClose?: () => void;
    /** 自定义类名 */
    class?: string;
    /** 右侧插槽 */
    right?: Snippet;
    /** 页面内容插槽 */
    children?: Snippet;
    /** 状态栏区域固定背景色 (hex 格式)，用于深色背景页面确保状态栏文字可见 */
    statusBarBgColor?: string;
    /** 禁用向上偏移（某些项目不需要偏移适配） */
    disableOffsetUp?: boolean;
    /** 自动检测 URL 中的 ?rule=true 参数，禁用向上偏移（默认 true） */
    autoDetectRule?: boolean;
    /** 启用文字颜色跟随滚动渐变（初始使用 initialTextColor，滚动后渐变到 lightTextColor/darkTextColor） */
    textColorFade?: boolean;
    /** 初始文字颜色（textColorFade=true 时生效，滚动前的文字颜色） */
    initialTextColor?: string;
  }

  let {
    title = "",
    titleCustom,
    lightBgColor = "#ffffff",
    darkBgColor = "#0a0a0a",
    lightTextColor = "#000000",
    darkTextColor = "#ffffff",
    bgOpacity = 1,
    scrollFade = true,
    scrollThreshold = 20,
    maxOpacity = 1,
    showBack = true,
    onBack,
    onClose,
    class: className = "",
    right,
    children,
    statusBarBgColor,
    disableOffsetUp = false,
    autoDetectRule = true,
    textColorFade = false,
    initialTextColor = "#000000",
  }: Props = $props();

  // 获取顶部安全区高度
  const headerMargin = parseInt(storageModel.headerMargin || "0", 10);

  // 是否显示 NavBar：iOS/Android 环境 且 已知 App
  const shouldShow = shouldShowNavBar();

  // 自动检测是否为 Rule 页面（响应式，监听路由变化）
  // 覆盖三种场景：?rule=true、?rules=true、hash 路由含 /rule 或 /rules
  function detectRulePage(): boolean {
    if (!autoDetectRule) return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get("rule") === "true" || params.get("rules") === "true") {
      return true;
    }
    const hash = window.location.hash;
    return /[#/]rules?\b/.test(hash);
  }
  let isRulePage = $state(detectRulePage());

  // 监听 hash 变化，重新检测 rule 页面
  onMount(() => {
    function onHashChange() {
      isRulePage = detectRulePage();
      // 路由切换回到顶部，并同步滚动渐变状态
      if (contentEl) contentEl.scrollTop = 0;
      scrollTop = 0;
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  });

  // 是否需要向上偏移：只在显示 NavBar 且 App 版本不达标（自带导航条）时抵消预留空间
  // Rule 页面不需要偏移
  const _shouldOffsetUp = shouldOffsetUp();
  let needOffsetUp = $derived(
    disableOffsetUp || isRulePage ? false : _shouldOffsetUp,
  );

  // NavBar 总高度：旧版本 App 下不含状态栏高度（状态栏由 App 管理）
  const navBarHeight = _shouldOffsetUp ? 44 : 44 + headerMargin;
  // 旧版本 App 下状态栏由 App 管理，NavBar 不需要状态栏 padding
  const navBarPaddingTop = _shouldOffsetUp ? 0 : headerMargin;

  // App 预留空间 88px（状态栏 44 + 导航条 44）
  // 显示 NavBar 时只偏移 44px（吃掉 App 导航条空间，状态栏留给 NavBar 背景覆盖）
  // 不显示 NavBar 时（浏览器环境）偏移完整 88px
  const APP_RESERVED_SPACE = 88;
  const NAV_BAR_ONLY = 44;
  let offsetUpValue = $derived(
    needOffsetUp ? (shouldShow ? NAV_BAR_ONLY : APP_RESERVED_SPACE) : 0,
  );

  const isDarkMode = storageModel.isDarkMode === "Y";
  // 目标文字颜色（滚动后的颜色）
  // 必须保持响应式：同一个 NavBar 实例在 hash 路由切换时（如首页 → 规则页），
  // 上层会动态改变 lightTextColor / darkTextColor，写成常量会导致颜色停留在初始值
  let targetTextColor = $derived(isDarkMode ? darkTextColor : lightTextColor);

  // 状态栏固定背景色（如果设置了的话）
  const statusBarBg = statusBarBgColor || null;

  // 将 CSS 变量设置到 :root，确保所有元素都能访问
  onMount(() => {
    document.documentElement.style.setProperty(
      "--header-margin",
      `${headerMargin}px`,
    );
    const navBarHeightValue = shouldShow ? `${navBarHeight}px` : "0px";
    document.documentElement.style.setProperty(
      "--nav-bar-height",
      navBarHeightValue,
    );
    // 设置向上偏移量
    document.documentElement.style.setProperty(
      "--offset-up",
      `${offsetUpValue}px`,
    );
  });

  // 将 hex 颜色转换为 rgb（支持 3 位简写和 6 位完整格式）
  function hexToRgb(hex: string): string {
    // 先处理简写 #abc -> #aabbcc
    const fullHex = hex.replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      "#$1$1$2$2$3$3",
    );
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    if (result) {
      return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
    }
    return "255, 255, 255";
  }

  // 颜色插值函数：根据透明度在两个颜色之间插值
  function interpolateColor(
    color1: string,
    color2: string,
    factor: number,
  ): string {
    const rgb1 = hexToRgb(color1).split(", ").map(Number);
    const rgb2 = hexToRgb(color2).split(", ").map(Number);
    const r = Math.round(rgb1[0] + (rgb2[0] - rgb1[0]) * factor);
    const g = Math.round(rgb1[1] + (rgb2[1] - rgb1[1]) * factor);
    const b = Math.round(rgb1[2] + (rgb2[2] - rgb1[2]) * factor);
    return `rgb(${r}, ${g}, ${b})`;
  }

  // 响应式背景色：路由切换时上层可动态改变 lightBgColor / darkBgColor
  let bgColorRgb = $derived(
    isDarkMode ? hexToRgb(darkBgColor) : hexToRgb(lightBgColor),
  );

  // 滚动相关状态
  let scrollTop = $state(0);
  let contentEl: HTMLDivElement | null = $state(null);

  // 计算透明度
  let computedOpacity = $derived(
    scrollFade
      ? scrollTop <= scrollThreshold
        ? 0
        : Math.min((scrollTop - scrollThreshold) / 100, maxOpacity)
      : bgOpacity,
  );

  // 文字颜色：如果启用 textColorFade，则根据滚动透明度插值
  // 注意：maxOpacity 为 0 时使用 factor=1 避免除零产生 NaN
  let textColor = $derived(
    textColorFade && scrollFade
      ? interpolateColor(
          initialTextColor,
          targetTextColor,
          maxOpacity > 0 ? computedOpacity / maxOpacity : 1,
        )
      : targetTextColor,
  );

  // 通知 App 状态栏文字颜色（致富通 + 版本满足 + 纯白/纯黑时生效）
  $effect(() => {
    notifyStatusTextColor(textColor);
  });

  // 响应式背景样式
  let bgStyle = $derived(`rgba(${bgColorRgb}, ${computedOpacity})`);

  function handleScroll(e: Event) {
    const target = e.target as HTMLElement;
    scrollTop = target.scrollTop;
  }

  function handleBack() {
    if (onBack && onBack() === false) {
      return;
    }
    if (onClose) {
      onClose();
    }
  }

  // 连续点击标题 10 次打开 eruda 调试工具（5 秒内）
  let titleClickCount = 0;
  let titleClickTimer: ReturnType<typeof setTimeout> | null = null;

  function loadEruda(src: string, fallbackSrc?: string) {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => {
      // @ts-ignore
      window.eruda?.init();
    };
    if (fallbackSrc) {
      script.onerror = () => loadEruda(fallbackSrc);
    }
    document.head.appendChild(script);
  }

  function handleTitleClick() {
    titleClickCount++;
    if (titleClickTimer) clearTimeout(titleClickTimer);
    titleClickTimer = setTimeout(() => {
      titleClickCount = 0;
    }, 10000);

    if (titleClickCount >= 10) {
      titleClickCount = 0;
      if (titleClickTimer) clearTimeout(titleClickTimer);
      // @ts-ignore
      if (window.eruda?._isInit) return;
      loadEruda(
        "https://unpkg.com/eruda",
        "https://cdn.jsdelivr.net/npm/eruda",
      );
    }
  }
</script>

<div
  class="nav-wrapper {className}"
  style="
    --header-margin: {headerMargin}px;
    --nav-bar-height: {shouldShow ? navBarHeight : 0}px;
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  "
>
  <!-- NavBar 固定头部 -->
  {#if shouldShow}
    <!-- 状态栏区域固定背景（用于深色背景页面） -->
    {#if statusBarBg && headerMargin > 0}
      <div
        class="status-bar-bg"
        style="
          position: fixed;
          top: 0;
          left: 0;                                                                                                                                
          right: 0;
          height: {headerMargin}px;
          background: {statusBarBg};
          z-index: 101;
          pointer-events: none;
        "
      ></div>
    {/if}
    <div
      class="nav-bar"
      style="
        height: {navBarHeight}px;
        padding-top: {navBarPaddingTop}px;
        background: {bgStyle};
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 100;
        display: flex;
        align-items: center;
        padding-left: 16px;
        padding-right: 16px;
        box-sizing: border-box;
        pointer-events: none;
      "
    >
      <!-- 左侧按钮 -->
      <div
        class="nav-left"
        style="position: relative; z-index: 1; min-width: 24px; pointer-events: auto;"
      >
        {#if showBack}
          <button
            class="nav-back"
            style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; background: none; border: none; padding: 0; cursor: pointer;"
            onclick={handleBack}
            aria-label="返回"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 19L8 12L15 5"
                stroke={textColor}
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        {/if}
      </div>

      <!-- 标题居中 -->
      <div
        class="nav-title"
        style="
          position: absolute;
          left: 60px;
          right: 60px;
          z-index: 2;
          text-align: center;
          font-size: 18px;
          font-weight: 600;
          color: {textColor};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          pointer-events: auto;
          cursor: default;
          -webkit-user-select: none;
          user-select: none;
          display: flex;
          align-items: center;
          justify-content: center;
        "
        onclick={handleTitleClick}
        role="presentation"
      >
        {#if titleCustom}
          {@render titleCustom()}
        {:else}
          <!--
            省略号必须落在这个内层 span 上：外层 .nav-title 是 flex 容器，
            文本会成为匿名 flex item，text-overflow 对 flex 容器自身不生效，
            结果只会被裁切而不显示 "…"
          -->
          <span
            style="
              display: block;
              min-width: 0;
              max-width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            ">{title}</span
          >
        {/if}
      </div>

      <!-- 右侧插槽 -->
      <div
        class="nav-right"
        style="position: relative; z-index: 1; min-width: 24px; margin-left: auto; pointer-events: auto;"
      >
        {#if right}
          {@render right()}
        {/if}
      </div>
    </div>
  {/if}

  <!-- 内容区（滚动容器） -->
  <div
    class="nav-content"
    bind:this={contentEl}
    style="
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      {needOffsetUp ? `margin-top: -${offsetUpValue}px;` : ''}
    "
    onscroll={handleScroll}
  >
    {#if children}
      {@render children()}
    {/if}
  </div>
</div>
